import React from 'react';

import { Box, Button, Typography } from '@mui/material';
import { isNotNil, upperFirst } from 'es-toolkit';
import { DateTime } from 'luxon';

import { parseNullableValue } from '@hrlogics/helper-utils';

import { DateFormatPatterns, VERIFICATION_REQUEST_OUTDATED_DAYS } from '@clearverify/constants';
import { formatDate, getDateTimeInTimezone } from '@clearverify/utils';

import {
  PermissiblePurposeStatus,
  useGetEmployersListForVerificationRequestQuery,
  useVerificationRequestPaymentInfoQuery,
  VerificationRequestStatus,
  VerificationRequestStripePaymentStatus,
} from 'src/api';
import { InfoBlock } from 'src/components/shared';
import { reportAvailableVerificationRequestStatuses } from 'src/constants/inbox';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useScreenInfo } from 'src/hooks/common';
import { useEmployeeInfoForVerificationRequest } from 'src/hooks/employee';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { usePermissiblePurpose } from 'src/hooks/permissiblePurposes';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser } from 'src/hooks/user';
import {
  useVerificationRequest,
  useVerificationRequestPaymentInfoSubscription,
} from 'src/hooks/verificationRequest';
import { useCurrentUserVerifier } from 'src/hooks/verifier';
import { ReverificationSubmitByVerifierStaffModalArgs } from 'src/modals/verificationRequest';
import { ViewVerificationRequestReportModalArgs } from 'src/modals/verifier';
import { VerificationRequestFormProvider } from 'src/providers/VerificationRequestFormProvider';
import type { SxProp } from 'src/types/mui';

import { InboxItemContentSubmitReverificationButton } from './components';
import { InboxItemContentVerificationRequestReviewInformationForm } from './InboxItemContentVerificationRequestReviewInformationForm';
import { requestBodyContainerStyles, viewReportButtonStyles } from './shared/styles';
import { getBlockInformationHeading } from './utils';

export const InboxItemContentVerificationRequestVerifierStaff: React.FC = () => {
  const { isMobile } = useScreenInfo();
  const { openModal } = useModal();

  const { timezone } = useCurrentUser();
  const { isGovernmentVerifier } = useCurrentUserVerifier();

  const inboxItemId = useInboxItemParamId();

  const { verificationRequest } = useVerificationRequest({ inboxItemId });
  const { data: verificationRequestPaymentInfo } = useVerificationRequestPaymentInfoQuery({
    variables: {
      where: { inboxItemId },
    },
    skip: !inboxItemId,
  });

  const { permissiblePurpose } = usePermissiblePurpose({
    id: verificationRequest?.permissiblePurposeId,
  });
  const { generatedReportsMeta, loading } = useGeneratedReports({ inboxItemId });
  const { data: employersData } = useGetEmployersListForVerificationRequestQuery({
    variables: {
      inboxItemId: inboxItemId || '',
    },
    skip: !inboxItemId,
  });
  const { employeeInfoForVerificationRequest, loading: employeeInfoForVerificationRequestLoading } =
    useEmployeeInfoForVerificationRequest(verificationRequest?.employeeId);

  useVerificationRequestPaymentInfoSubscription({ inboxItemId, skip: isGovernmentVerifier });

  const verificationRequestStatus = React.useMemo(() => {
    return verificationRequest?.status;
  }, [verificationRequest?.status]);

  const isOutdatedVerificationRequest = React.useMemo(() => {
    if (!verificationRequest?.createdAt) return true;
    return (
      Math.abs(DateTime.fromISO(verificationRequest.createdAt).diffNow('days').days) >=
      VERIFICATION_REQUEST_OUTDATED_DAYS
    );
  }, [verificationRequest?.createdAt]);

  const isSubmitVerificationButtonVisible = React.useMemo(() => {
    return (
      !isOutdatedVerificationRequest &&
      verificationRequestStatus === VerificationRequestStatus.ReviewedByVerifier
    );
  }, [isOutdatedVerificationRequest, verificationRequestStatus]);

  const reportIsGenerated = React.useMemo(
    () => generatedReportsMeta.some(isNotNil),
    [generatedReportsMeta],
  );

  const isReportAvailable = React.useMemo(
    () =>
      verificationRequestStatus &&
      reportAvailableVerificationRequestStatuses.includes(verificationRequestStatus),
    [verificationRequestStatus],
  );

  const isVerifierToRespondStatus =
    verificationRequestStatus === VerificationRequestStatus.VerifierToRespond;

  const isPermissiblePurposeApproved =
    permissiblePurpose?.status === PermissiblePurposeStatus.Approved;

  const stripePaymentStatus = React.useMemo(
    () => verificationRequestPaymentInfo?.verificationRequest?.stripePaymentStatus,
    [verificationRequestPaymentInfo?.verificationRequest?.stripePaymentStatus],
  );

  const employerIsLive = React.useMemo(() => {
    return !employersData?.getEmployersListForVerificationRequest.isThereInactiveEmployers;
  }, [employersData?.getEmployersListForVerificationRequest.isThereInactiveEmployers]);

  const openSubmitReverificationModal = React.useCallback(() => {
    if (!inboxItemId) return;
    openModal(ModalId.ReverificationSubmitByVerifierStaff, {
      inboxItemId,
    } satisfies ReverificationSubmitByVerifierStaffModalArgs);
  }, [inboxItemId, openModal]);

  const generatedReportCreatedAt = React.useMemo(() => {
    const date = generatedReportsMeta[0]?.createdAt || new Date();
    return formatDate(
      getDateTimeInTimezone(date, timezone)?.toISO(),
      DateFormatPatterns.meridiemTimePaddedWithDate,
    );
  }, [generatedReportsMeta, timezone]);

  const handleViewReportClick = React.useCallback(() => {
    openModal(ModalId.ViewVerificationRequestReportModal, {
      verificationRequest,
      generatedReportsMeta,
      inboxItemId,
    } satisfies ViewVerificationRequestReportModalArgs);
  }, [generatedReportsMeta, inboxItemId, openModal, verificationRequest]);

  if (!inboxItemId) {
    return null;
  }

  if (isVerifierToRespondStatus) {
    const isPaymentProcessing =
      stripePaymentStatus === VerificationRequestStripePaymentStatus.Processing;

    return (
      <Box sx={blockInformationWrapperStyles}>
        <Typography variant="h6" lineHeight="28px" mb={2}>
          {getBlockInformationHeading(verificationRequest?.type)}
          sent by {parseNullableValue(employeeInfoForVerificationRequest?.fullName)}
        </Typography>

        <InfoBlock.Container
          sx={{ gridTemplateColumns: 'auto auto' }}
          loading={employeeInfoForVerificationRequestLoading}
        >
          <InfoBlock.Item
            title="Employee Full Name"
            value={employeeInfoForVerificationRequest?.fullName}
          />
          <InfoBlock.Item title="Contact Email" value={employeeInfoForVerificationRequest?.email} />
          <InfoBlock.Item
            title="Contact Phone"
            value={employeeInfoForVerificationRequest?.phoneNumber}
          />
          {stripePaymentStatus && (
            <InfoBlock.Item title="Payment Status" value={upperFirst(stripePaymentStatus)} />
          )}
        </InfoBlock.Container>

        <VerificationRequestFormProvider>
          <InboxItemContentVerificationRequestReviewInformationForm
            inboxItemId={inboxItemId}
            disabled={isPaymentProcessing}
          />
        </VerificationRequestFormProvider>
      </Box>
    );
  }

  if (isReportAvailable) {
    return (
      <Box sx={requestBodyContainerStyles}>
        <Typography variant="h6" mb={1}>
          {verificationRequest?.type
            ? VERIFICATION_REQUEST_TYPE_LABEL[verificationRequest.type] || 'Verification'
            : 'Verification'}{' '}
          report has been created
        </Typography>

        <Typography variant="body2">
          Please note that report contents are updated as of {generatedReportCreatedAt}.
          {reportIsGenerated
            ? 'To view this report, click the “view report” button below. Upon clicking this button you will be able to download or email the report.'
            : 'The report will be available after the payment is processed.'}
        </Typography>

        <Box
          display="flex"
          flexDirection="row"
          justifyContent={
            isMobile && verificationRequestStatus === VerificationRequestStatus.ReviewedByVerifier
              ? 'center'
              : 'flex-end'
          }
          mt={2.5}
          columnGap={isMobile ? 1 : 2}
        >
          {isSubmitVerificationButtonVisible && (
            <InboxItemContentSubmitReverificationButton
              isApproved={isPermissiblePurposeApproved}
              employerIsLive={employerIsLive}
              openSubmitReverificationModal={openSubmitReverificationModal}
              isGovernmentVerifier={isGovernmentVerifier}
            />
          )}

          {reportIsGenerated && (
            <Button
              color={isSubmitVerificationButtonVisible ? 'infoButton' : 'primary'}
              variant={isSubmitVerificationButtonVisible ? 'outlined' : 'contained'}
              sx={viewReportButtonStyles}
              onClick={handleViewReportClick}
              loading={loading}
            >
              View report
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return null;
};

const blockInformationWrapperStyles: SxProp = theme => ({
  width: '100%',
  border: theme.constantStyles?.paperBorder,
  borderRadius: theme.constantStyles?.paperBorderRadius,
  height: '100%',
  overflow: 'hidden',
  backgroundColor: 'transparent',
  padding: 3,
});
